import React, { useState, useRef, useCallback, useMemo } from 'react'

import { CaretDown } from 'phosphor-react'
import { twMerge } from 'tailwind-merge'

import { useOuterClickNotifier, useHotkey } from '../../../../hooks'
import { Checkbox } from '../../../v2/Checkbox'
import { Icon } from '../../../v2/Icons'
import { Text } from '../../../v2/Text'

export const MultiSelectInputField = ({
	register,
	name,
	onChange,
	value,
	label,
	placeholder,
	options = [],
	error,
	disabled,
	icon,
	variant,
	className,
	id,
	...props
}) => {
	const containerRef = useRef(null)
	const [isOpen, setIsOpen] = useState(false)

	useHotkey('Escape', () => isOpen && setIsOpen(false))

	const closeDropdown = useCallback(() => setIsOpen(false), [])
	useOuterClickNotifier(closeDropdown, containerRef)

	const parsedValue = useMemo(() => {
		if (typeof value === 'string') {
			return value ? [value] : []
		}
		return value || []
	}, [value])

	const handleClick = useCallback(
		(newValue) => {
			if (parsedValue.includes(newValue)) {
				return onChange({
					target: {
						value: parsedValue.filter((val) => newValue !== val),
						name
					}
				})
			}

			return onChange({
				target: { value: [...parsedValue, newValue], name }
			})
		},
		[name, onChange, parsedValue]
	)

	const displayText = useMemo(() => {
		const labels = parsedValue
			.map((val) => options.find((option) => option.value === val)?.label)
			.filter(Boolean)
		return labels.length ? labels.join(', ') : placeholder
	}, [options, parsedValue, placeholder])

	const hasSelection = parsedValue.length > 0

	const hasValue = useCallback(
		(currentValue) => parsedValue.includes(currentValue),
		[parsedValue]
	)

	return (
		<div
			ref={containerRef}
			className={twMerge('relative flex w-full flex-col', className)}
			{...props}
		>
			<button
				type='button'
				id={id}
				name={name}
				disabled={disabled}
				aria-expanded={isOpen}
				aria-haspopup='listbox'
				onClick={() => !disabled && setIsOpen(!isOpen)}
				className={twMerge(
					'group w-full !rounded-lg border !border-gray-300 bg-white !px-4 !py-2 text-left text-sm font-normal outline-none transition-colors hover:!border-orange-600 hover:!bg-[#FFF1E6] focus-visible:!border-orange-600 focus-visible:outline-none disabled:cursor-not-allowed disabled:!border-[#E7EAED] disabled:!bg-gray-100 disabled:text-gray-400',
					error && '!border-red-500',
					variant === 'primary' && Boolean(label) && '!pt-2'
				)}
			>
				{variant === 'primary' && label ? (
					<label
						htmlFor={id}
						className='!mb-1 block text-sm font-normal !text-orange-600'
					>
						{label}
					</label>
				) : null}
				{variant !== 'primary' && label ? (
					<label
						htmlFor={id}
						className={twMerge(
							'!mb-1 block text-sm font-normal',
							hasSelection ? 'text-gray-700' : '!text-gray-400'
						)}
					>
						{label}
					</label>
				) : null}
				<div className='flex w-full items-center justify-between gap-4'>
					<div className='flex min-w-0 flex-1 items-center gap-2 overflow-hidden'>
						{icon ? (
							<Icon
								name={icon}
								className={
									variant === 'dark' ? 'fill-gray-950' : 'fill-orange-600'
								}
							/>
						) : null}
						<span
							className={twMerge(
								'block truncate text-left text-base',
								hasSelection ? '!text-gray-700' : '!text-gray-400'
							)}
						>
							{displayText}
						</span>
					</div>
					{!disabled ? (
						<CaretDown
							size={16}
							color={variant === 'dark' ? '#000' : '#FF7708'}
							className='shrink-0'
						/>
					) : null}
				</div>
			</button>
			{isOpen && options.length > 0 && (
				<div className='absolute left-0 right-0 top-full z-[1000] !mt-2 max-h-56 w-full overflow-y-auto rounded-lg border border-solid border-gray-300 bg-white py-1 shadow-xl'>
					<ul className='m-0 list-none p-0'>
						{options.map((option) => (
							<li
								key={option.value}
								tabIndex={0}
								className={twMerge(
									'cursor-pointer px-4 py-3 text-sm text-gray-800 outline-none transition-colors',
									hasValue(option.value)
										? 'bg-gray-100 font-semibold'
										: 'hover:bg-gray-100'
								)}
								onClick={() => handleClick(option.value)}
								onKeyUp={(event) => {
									if (event.key === 'Enter') {
										handleClick(option.value)
									}
								}}
							>
								<Checkbox
									register={register}
									value={hasValue(option.value)}
									checked={hasValue(option.value)}
									name={String(option.value)}
									label={option.label}
									onClick={() => handleClick(option.value)}
									selectOverflow
								/>
							</li>
						))}
					</ul>
				</div>
			)}
			{error && !value ? (
				<Text className='!mt-1 !pl-4 text-left text-xs !text-red-500'>
					{error}
				</Text>
			) : null}
		</div>
	)
}
